import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/upr-api";
import AudioPlayer from "@/components/AudioPlayer";
import { ArrowLeft, ExternalLink, Share2 } from "lucide-react";
import DOMPurify from "dompurify";

const RICH_TEXT_SANITIZE = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "u", "a", "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "hr"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
  // NOTE: KEEP_CONTENT defaults to true — do NOT set it to false. Setting
  // false makes DOMPurify strip the inner text of allowed tags like <p>, which
  // was the bug that hid all editor content on the public pages.
};

const LABEL_BANDCAMP_URL = "https://universopositivo.bandcamp.com/";

function toEmbed(url) {
  if (!url) return "";
  // Convert YouTube watch links to embed
  const yt = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const ytShort = url.match(/youtu\.be\/([^?]+)/);
  if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;
  return url;
}

export default function ReleaseDetail() {
  const { id } = useParams();
  const [release, setRelease] = useState(null);
  const [featuredIn, setFeaturedIn] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/releases/${id}`)
      .then(({ data }) => setRelease(data))
      .catch(() => setNotFound(true));
    // Load playlists that reference this release — silent-fail if unavailable.
    api.get(`/playlists/_by_release/${id}`)
      .then(({ data }) => setFeaturedIn(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="px-6 md:px-12 lg:px-16 py-24" data-testid="release-not-found">
        <Link to="/" className="upr-link-underline text-sm">← Back to Music</Link>
        <h1 className="font-heading font-black text-5xl mt-8">Release not found.</h1>
      </div>
    );
  }
  if (!release) {
    return <div className="px-6 md:px-12 lg:px-16 py-24" data-testid="release-loading">Loading…</div>;
  }

  const isBandcamp = release.embed_url && release.embed_url.includes("bandcamp.com");
  const ytEmbed = release.embed_url && (release.embed_url.includes("youtube") || release.embed_url.includes("youtu.be"))
    ? toEmbed(release.embed_url) : null;

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16" data-testid="release-detail">
      <Link to="/" className="inline-flex items-center gap-1 upr-link-underline text-sm" data-testid="back-link">
        <ArrowLeft size={14} /> Back to Music
      </Link>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12 mt-8">
        <div className="md:col-span-5 lg:col-span-5">
          <div className="upr-cover aspect-square" style={{ border: "1px solid var(--upr-border)" }}>
            <img src={release.cover_url} alt={release.title} className="w-full h-full object-cover" data-testid="release-cover" />
          </div>

          {release.buy_url ? (
            <a href={release.buy_url} target="_blank" rel="noreferrer" className="upr-btn w-full mt-5" data-testid="buy-button">
              Buy on Bandcamp <ExternalLink size={14} />
            </a>
          ) : (
            <a
              href={LABEL_BANDCAMP_URL}
              target="_blank"
              rel="noreferrer"
              className="upr-btn w-full mt-5"
              data-testid="buy-button-fallback"
              title="Open the label's Bandcamp"
            >
              Listen on Bandcamp <ExternalLink size={14} />
            </a>
          )}

          {release.tracks && release.tracks.length > 0 && (
            <div className="mt-5">
              <AudioPlayer tracks={release.tracks} />
            </div>
          )}

          {(isBandcamp || ytEmbed) && (
            <div className="mt-5" data-testid="embed-player">
              {isBandcamp && (
                <iframe title="Bandcamp player" src={release.embed_url} style={{ border: 0, width: "100%", height: 470 }} seamless />
              )}
              {ytEmbed && (
                <div className="aspect-video w-full" style={{ border: "1px solid var(--upr-border)" }}>
                  <iframe title="YouTube player" src={ytEmbed} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="md:col-span-7 lg:col-span-7">
          <div className="upr-eyebrow mb-3">{release.catalog_number || "—"} · {release.format} · {release.year || ""}</div>
          <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-6xl lg:text-7xl" style={{ letterSpacing: "-0.035em" }} data-testid="release-title">
            {release.title}
          </h1>
          {release.version && (
            <div className="mt-2 upr-eyebrow" data-testid="release-version" style={{ fontSize: "0.78rem" }}>
              {release.version}
            </div>
          )}
          <div className="mt-3 font-heading font-bold text-2xl md:text-3xl">
            {release.artist_id ? (
              <Link to={`/artist/${release.artist_id}`} className="upr-link-underline" data-testid="release-artist-link">{release.artist_name}</Link>
            ) : (
              <span>{release.artist_name}</span>
            )}
          </div>

          {release.description && (
            <div
              className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl upr-prose"
              data-testid="release-description"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(release.description, RICH_TEXT_SANITIZE) }}
            />
          )}

          {release.tracklist?.length > 0 && (
            <div className="mt-10" data-testid="tracklist">
              <div className="upr-eyebrow mb-3">Tracklist</div>
              <ol className="border-t" style={{ borderColor: "var(--upr-border)" }}>
                {release.tracklist.map((t, i) => (
                  <li key={i} className="flex items-center justify-between py-3 border-b" style={{ borderColor: "var(--upr-border)" }}>
                    <span className="flex items-center gap-4">
                      <span className="font-heading font-extrabold text-sm w-6 text-right" style={{ color: "var(--upr-muted)" }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-body">{t}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {release.credits && (
            <div className="mt-10" data-testid="credits">
              <div className="upr-eyebrow mb-2">Credits</div>
              <div
                className="text-sm leading-relaxed max-w-2xl upr-prose"
                style={{ color: "var(--upr-muted)" }}
                data-testid="release-credits"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(release.credits, RICH_TEXT_SANITIZE) }}
              />
            </div>
          )}

          {release.tags && release.tags.length > 0 && (
            <div className="mt-10" data-testid="tags">
              <div className="upr-eyebrow mb-3">Tags</div>
              <div className="flex flex-wrap gap-2">
                {release.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/?tag=${encodeURIComponent(String(t).toLowerCase())}`}
                    data-testid={`tag-${String(t).toLowerCase().replace(/\s+/g, "-")}`}
                    className="px-3 py-1 text-xs font-heading transition-colors uppercase"
                    style={{
                      background: "var(--upr-surface)",
                      border: "1px solid var(--upr-border)",
                      color: "var(--upr-fg)",
                      letterSpacing: "0.04em",
                    }}
                    title={`Browse releases tagged with “${t}”`}
                  >
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-4 text-sm" style={{ color: "var(--upr-muted)" }}>
            {release.release_date && (
              <span className="upr-eyebrow" data-testid="release-date">Released {release.release_date}</span>
            )}
            {release.upc && (
              <span className="upr-eyebrow" data-testid="release-upc">UPC {release.upc}</span>
            )}
            <button
              type="button"
              onClick={async () => {
                const url = window.location.href;
                try {
                  if (navigator.share) await navigator.share({ title: release.title, url });
                  else { await navigator.clipboard.writeText(url); }
                } catch (e) {
                  // User cancelled the share dialog or clipboard write — silent by design.
                  if (e?.name && e.name !== "AbortError") console.warn("[share]", e);
                }
              }}
              className="upr-eyebrow inline-flex items-center gap-1 hover:text-[color:var(--upr-primary)]"
              data-testid="share-button"
            >
              <Share2 size={12} /> Share
            </button>
          </div>

          {featuredIn.length > 0 && (
            <div className="mt-14 pt-8" style={{ borderTop: "1px solid var(--upr-border)" }} data-testid="featured-in">
              <div className="upr-eyebrow mb-4">Featured in</div>
              <ul className="flex flex-wrap gap-2">
                {featuredIn.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/playlists/${p.slug}`}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-heading uppercase tracking-widest transition-colors hover:bg-[color:var(--upr-fg)] hover:text-[color:var(--upr-bg)]"
                      style={{ border: "1px solid var(--upr-border)" }}
                      data-testid={`featured-in-${p.slug}`}
                    >
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
